import { TextField, TextFieldProps, Typography, SxProps, Theme, Box } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';

export interface NumericInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  /** Value in cents (e.g., 12345 for $123.45) */
  value?: number | null;
  /** Callback that receives value in cents, or null if field is empty */
  onChange?: (valueInCents: number | null) => void;
  /** Show dollar sign prefix */
  showCurrency?: boolean;
  /** Maximum number of decimal places (default: 2) */
  decimalPlaces?: number;
  /** Allow negative values (default: false) */
  allowNegative?: boolean;
  /** Maximum value in cents */
  maxValue?: number;
  /** Minimum value in cents */
  minValue?: number;
  /** Visual style variant: 'standard' (default MUI) or 'compact' (blue background, label above) */
  styleVariant?: 'standard' | 'compact';
  /** Optional suffix to display after the input (e.g., "miles", "gal") */
  suffix?: string;
  /** Show error state with red background */
  hasError?: boolean;
}

/**
 * Universal numeric input component for currency and numeric values.
 *
 * Features:
 * - Stores values as cents (integers) to avoid floating point errors
 * - Auto-formats on blur with proper decimal places
 * - Adds thousand separators for readability
 * - Removes formatting on focus for easy editing
 * - Prevents invalid characters
 * - Optional dollar sign prefix for currency
 * - Configurable decimal places for different use cases
 *
 * @example
 * // Currency input
 * <NumericInput
 *   value={12345} // $123.45
 *   onChange={(cents) => setValue(cents)}
 *   showCurrency={true}
 * />
 *
 * @example
 * // Numeric input (e.g., gallons with 3 decimals)
 * <NumericInput
 *   value={12345} // 12.345
 *   onChange={(value) => setValue(value)}
 *   decimalPlaces={3}
 * />
 */
export const NumericInput = React.forwardRef<HTMLDivElement, NumericInputProps>(
  (
    {
      value,
      onChange,
      showCurrency = false,
      decimalPlaces = 2,
      allowNegative = false,
      maxValue,
      minValue,
      styleVariant = 'standard',
      suffix,
      hasError = false,
      onFocus,
      onBlur,
      InputProps,
      ...textFieldProps
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Convert cents to display string (e.g., 12345 -> "123.45")
    const centsToDisplay = useCallback(
      (cents: number): string => {
        const divisor = Math.pow(10, decimalPlaces);
        return (cents / divisor).toFixed(decimalPlaces);
      },
      [decimalPlaces]
    );

    // Convert display string to cents (e.g., "123.45" -> 12345)
    const displayToCents = useCallback(
      (display: string): number => {
        const multiplier = Math.pow(10, decimalPlaces);
        const num = parseFloat(display) || 0;
        return Math.round(num * multiplier);
      },
      [decimalPlaces]
    );

    // Format display value with thousand separators (e.g., "1234.56" -> "1,234.56")
    const formatWithCommas = useCallback((value: string): string => {
      if (!value) return '';
      const parts = value.split('.');
      const sign = value.startsWith('-') ? '-' : '';
      const intValue = parseInt(parts[0].replace('-', ''), 10);
      if (Number.isNaN(intValue)) return value;
      parts[0] = `${sign}${intValue.toLocaleString('en-US')}`;
      return parts.join('.');
    }, []);

    // Initialize display value from cents prop
    useEffect(() => {
      if (!isFocused) {
        if (value === undefined || value === null) {
          // Field is null/undefined - show empty (placeholder will show)
          setDisplayValue('');
        } else {
          // Field has a value (including 0) - format and display it
          const formattedValue = centsToDisplay(value);
          setDisplayValue(formatWithCommas(formattedValue));
        }
      }
    }, [value, isFocused, centsToDisplay, formatWithCommas]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;

      // Handle negative sign if allowed
      const hasNegative = inputValue.startsWith('-');
      if (hasNegative && !allowNegative) {
        return; // Don't allow negative if not permitted
      }

      // Remove everything except numbers, decimal point, and optionally negative sign
      const regex = allowNegative ? /[^0-9.-]/g : /[^0-9.]/g;
      inputValue = inputValue.replace(regex, '');

      // Handle negative sign - only allow at the start
      if (allowNegative && inputValue.includes('-')) {
        const negCount = (inputValue.match(/-/g) || []).length;
        if (negCount > 1 || inputValue.indexOf('-') !== 0) {
          // Multiple negatives or negative not at start - remove all but first
          inputValue = (inputValue[0] === '-' ? '-' : '') + inputValue.replace(/-/g, '');
        }
      }

      // Only allow one decimal point
      const decimalCount = (inputValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        const parts = inputValue.split('.');
        inputValue = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limit to specified decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > decimalPlaces) {
          inputValue = parts[0] + '.' + parts[1].substring(0, decimalPlaces);
        }
      }

      setDisplayValue(inputValue);

      // Convert to cents and notify parent
      if (onChange) {
        const cents = displayToCents(inputValue);

        // Apply min/max constraints
        let constrainedCents = cents;
        if (maxValue !== undefined && cents > maxValue) {
          constrainedCents = maxValue;
        }
        if (minValue !== undefined && cents < minValue) {
          constrainedCents = minValue;
        }

        onChange(constrainedCents);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      // If field is null/undefined (placeholder state), clear it so user can type
      // If field has value (including 0), keep it so user can edit
      if (value === undefined || value === null) {
        setDisplayValue('');
      } else {
        // Remove commas for easier editing - keep the actual value (including 0)
        const valueWithoutCommas = displayValue.replace(/,/g, '');
        setDisplayValue(valueWithoutCommas);
      }

      // Call parent's onFocus if provided
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      let inputValue = displayValue;

      // Handle empty or invalid input - return null instead of 0
      if (!inputValue || inputValue === '-' || inputValue === '.') {
        // Field is truly empty, return null
        setDisplayValue('');
        if (onChange) {
          onChange(null);
        }
        if (onBlur) {
          onBlur(e);
        }
        return;
      }

      // Ensure proper decimal places
      if (!inputValue.includes('.')) {
        inputValue = decimalPlaces > 0 ? inputValue + '.' + '0'.repeat(decimalPlaces) : inputValue;
      } else {
        const parts = inputValue.split('.');
        const decimalPart = parts[1] || '';
        if (decimalPart.length < decimalPlaces) {
          inputValue = parts[0] + '.' + decimalPart.padEnd(decimalPlaces, '0');
        } else if (decimalPart.length === 0) {
          inputValue = decimalPlaces > 0 ? parts[0] + '.' + '0'.repeat(decimalPlaces) : parts[0];
        }
      }

      // Convert to cents and apply min/max constraints
      let cents = displayToCents(inputValue);
      if (maxValue !== undefined && cents > maxValue) {
        cents = maxValue;
      }
      if (minValue !== undefined && cents < minValue) {
        cents = minValue;
      }

      // Regenerate formatted display string from clamped cents
      const formattedValue = formatWithCommas(centsToDisplay(cents));
      setDisplayValue(formattedValue);

      // Emit the clamped value
      if (onChange) {
        onChange(cents);
      }

      // Call parent's onBlur if provided
      if (onBlur) {
        onBlur(e);
      }
    };

    // Determine if field is empty (for styling) - only if value is undefined/null
    const isEmpty = value === undefined || value === null;

    // Compact variant styles (blue background, label above)
    const compactStyles: SxProps<Theme> =
      styleVariant === 'compact'
        ? {
            '& .MuiInputBase-root': {
              backgroundColor: hasError ? '#ffcdd2' : 'primary.lighter',
              borderRadius: 2,
              minHeight: 'unset',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: hasError ? '#ffcdd2' : 'primary.lighter',
              },
              '&.Mui-focused': {
                backgroundColor: hasError ? '#ffcdd2' : 'primary.lighter',
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
              },
            },
            '& .MuiInputBase-input': {
              padding: '1px 10px',
              fontWeight: 600,
              color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'text.secondary',
              '&.Mui-disabled': {
                color: 'text.secondary',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputLabel-root': {
              position: 'relative',
              transform: 'none',
              marginBottom: 0.5,
              fontSize: '0.75rem',
              fontWeight: 400,
              color: 'text.secondary',
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              maxWidth: 'none',
              width: 'auto',
              '&.Mui-focused': {
                color: 'text.secondary',
              },
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            },
          }
        : {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const combinedSx: any = [
      textFieldProps.sx,
      compactStyles,
      {
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#424242 !important',
          color: 'text.secondary',
        },
        '& .MuiInputBase-input.Mui-disabled::placeholder': {
          opacity: 1,
          WebkitTextFillColor: '#424242',
          color: 'text.secondary',
        },
        '& .MuiInputBase-input::placeholder': {
          opacity: isEmpty ? 1 : 0.42,
          WebkitTextFillColor: isEmpty ? 'rgba(0, 0, 0, 0.38)' : undefined,
          color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : undefined,
        },
      },
    ];

    // Format value for display when disabled
    const formatDisabledValue = () => {
      const divisor = Math.pow(10, decimalPlaces);
      const displayNum = (value ?? 0) / divisor;

      const formatted = displayNum.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });

      if (showCurrency) {
        return `$${formatted}`;
      }

      return formatted;
    };

    // Render as plain text when disabled with suffix
    if (textFieldProps.disabled && suffix) {
      return (
        <Box ref={ref} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...textFieldProps.sx }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              padding: '1px 0',
            }}
          >
            {formatDisabledValue()}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1rem', fontWeight: 400 }}>
            {suffix}
          </Typography>
        </Box>
      );
    }

    const textField = (
      <TextField
        {...textFieldProps}
        ref={!suffix ? ref : undefined}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        variant="outlined"
        InputLabelProps={{
          shrink: styleVariant === 'standard' ? true : false,
          ...textFieldProps.InputLabelProps,
        }}
        InputProps={{
          ...InputProps,
          startAdornment: showCurrency ? (
            <Typography
              sx={{
                color: textFieldProps.disabled || !isEmpty ? 'text.secondary' : 'rgba(0, 0, 0, 0.38)',
                fontWeight: 600,
              }}
            >
              $
            </Typography>
          ) : (
            InputProps?.startAdornment
          ),
          inputProps: {
            ...InputProps?.inputProps,
            inputMode: 'decimal',
          },
        }}
        sx={combinedSx}
      />
    );

    // If suffix exists, wrap TextField with suffix outside the box
    if (suffix) {
      return (
        <Box ref={ref} sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, ...textFieldProps.sx }}>
          {textField}
          <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', fontWeight: 400, mb: -0.65 }}>
            {suffix}
          </Typography>
        </Box>
      );
    }

    return textField;
  }
);

NumericInput.displayName = 'NumericInput';
